import { useEffect, useState } from 'react';
import { useSystem } from '@ohif/core';
import { SeriesLabelService, SeriesLabels } from '../services/SeriesLabelService';

/**
 * Subscribe and select by series UID. The effect's dependencies are the two UIDs and NOT the
 * per-image instance, which is the whole difference from develop: its overlay item drove the
 * fetch off `instance` (CustomizableViewportOverlay.tsx:576-590 there), so scrolling a
 * 300-slice CT issued roughly 600 Orthanc requests for data that is constant per series
 * (§5.3). Here request() runs once per series, and the service's cache covers the second
 * viewport.
 *
 * useSystem() rather than the `servicesManager` prop the overlay item already receives:
 * SystemContextProvider mounts at App.tsx:119, above BrowserRouter and therefore above every
 * viewport, and useOrganReview.ts:32 sets the precedent for reaching services this way.
 */
export function useSeriesLabels(
  studyInstanceUid?: string,
  seriesInstanceUid?: string
): SeriesLabels | null {
  const { servicesManager } = useSystem();
  const { seriesLabelService } = servicesManager.services;

  const [entry, setEntry] = useState<SeriesLabels | null>(() =>
    seriesInstanceUid ? seriesLabelService.get(seriesInstanceUid) : null
  );

  useEffect(() => {
    // Both are undefined on the first renders, and whenever the item is mounted against a
    // non-CT series -- which is how QACheckItem expresses develop's modality gate without
    // calling this hook conditionally.
    if (!studyInstanceUid || !seriesInstanceUid) {
      setEntry(null);
      return undefined;
    }

    const apply = () => setEntry(seriesLabelService.get(seriesInstanceUid));

    const { unsubscribe } = seriesLabelService.subscribe(
      SeriesLabelService.EVENTS.SERIES_LABELS_CHANGED,
      event => {
        if (event.seriesInstanceUid === seriesInstanceUid) {
          apply();
        }
      }
    );

    // Subscribe BEFORE requesting. _broadcastEvent calls listeners inline
    // (pubSubServiceInterface.ts:83-95), so a request() that joins an in-flight promise and
    // resolves within the same turn would broadcast into the gap between these two lines.
    apply();
    void seriesLabelService.request(studyInstanceUid, seriesInstanceUid);

    return unsubscribe;
  }, [seriesLabelService, studyInstanceUid, seriesInstanceUid]);

  return entry;
}
